/**
 * The ONE definition of the account types a person can sign up as.
 *
 * Three separate pages used to each hardcode their own role array (register,
 * choose-role, kyc), and the kyc copy drifted so far it was still quoting
 * "From $20/mo" and "From $100/mo" long after those prices stopped existing.
 * Everything that offers a sign-up choice now reads this file instead.
 *
 * ACCOUNT TYPE vs ROLE — they are deliberately not the same thing:
 * `UserRole` in the database is BUYER | AGENT | OWNER | DEVELOPER | ADMIN, and
 * an agency is NOT a role. An agency is an AGENT who also owns an `Agency` row
 * (see `Agent.agencyId`). So AGENCY is a sign-up choice that maps onto the AGENT
 * role plus an agency record. Adding an AGENCY enum value instead would have
 * duplicated a relationship the schema already models.
 *
 * Client-safe: this imports only pure constants from plans.ts, no server code.
 */
import { PLAN_CONFIG, type PlanKey } from "@/lib/payments/plans"

export type AccountTypeId = "BUYER" | "AGENT" | "AGENCY" | "OWNER" | "DEVELOPER"
export type SellerRole = "AGENT" | "OWNER" | "DEVELOPER"
export type UserRoleValue = "BUYER" | SellerRole

export interface AccountType {
  id: AccountTypeId
  /** The UserRole actually written to the database. */
  role: UserRoleValue
  label: string
  desc: string
  icon: string
  /** Sellers must pass KYC before publishing, and are routed to /kyc after signup. */
  seller: boolean
  /**
   * When set, signup asks for an organisation name and that name is what the
   * public sees instead of the person's own name.
   */
  orgName?: { label: string; placeholder: string }
  /** Plan used for the "from" price quoted on the signup card. */
  entryPlan?: PlanKey
}

export const ACCOUNT_TYPES: AccountType[] = [
  {
    id: "BUYER", role: "BUYER", label: "Buyer / Renter", icon: "Search",
    desc: "Looking for a property", seller: false,
  },
  {
    id: "AGENT", role: "AGENT", label: "Agent", icon: "Building",
    desc: "Independent estate agent", seller: true, entryPlan: "AGENT_SOLO",
  },
  {
    id: "AGENCY", role: "AGENT", label: "Agency", icon: "Building",
    desc: "Agency with a team of agents", seller: true, entryPlan: "AGENCY",
    orgName: { label: "Agency name", placeholder: "e.g. Palm Leaf Real Estate" },
  },
  {
    id: "OWNER", role: "OWNER", label: "FSBO (Landlord)", icon: "Home",
    desc: "Selling or renting my own property", seller: true, entryPlan: "FSBO",
  },
  {
    id: "DEVELOPER", role: "DEVELOPER", label: "Developer", icon: "Development",
    desc: "Property developer", seller: true, entryPlan: "DEVELOPER",
    orgName: { label: "Company name", placeholder: "e.g. Daylesford Property Developments (Pvt) Ltd" },
  },
]

export const SELLER_ACCOUNT_TYPES = ACCOUNT_TYPES.filter(t => t.seller)

export function accountType(id: string | null | undefined): AccountType | undefined {
  return ACCOUNT_TYPES.find(t => t.id === id)
}

/** True when this sign-up choice needs an organisation name captured. */
export function needsOrgName(id: string | null | undefined): boolean {
  return !!accountType(id)?.orgName
}

/**
 * Best guess at the account type for an existing user, used to preselect the
 * right card when someone revisits a picker. An AGENT who already belongs to an
 * agency is an AGENCY; everyone else maps straight from their role.
 */
export function accountTypeForUser(
  user: { role?: string | null; companyName?: string | null } | null | undefined,
  hasAgency = false,
): AccountTypeId {
  if (!user?.role) return "BUYER"
  if (user.role === "AGENT") return hasAgency ? "AGENCY" : "AGENT"
  if (user.role === "OWNER" || user.role === "DEVELOPER" || user.role === "BUYER") return user.role
  return "BUYER"
}

/** Minimal shape of the effective-pricing object returned by /api/site-config. */
type PricingLike = Record<string, any> | null | undefined

/**
 * The price to quote on a signup card, read from live pricing with the code
 * defaults as fallback.
 *
 * Returns null while the free trial is active — during the trial nothing is
 * charged, so quoting a price would be simply untrue. Callers show the trial
 * line instead.
 */
export function entryPriceLabel(
  type: AccountType,
  pricing: PricingLike,
  trialActive: boolean,
): string | null {
  if (trialActive || !type.entryPlan) return null
  const plan = type.entryPlan
  const eff = pricing?.[plan] ?? {}
  const def = PLAN_CONFIG[plan]

  if (plan === "FSBO") {
    const fee = eff.listingFee ?? def.listingFee
    return fee ? `$${fee} per listing` : null
  }
  const monthly = eff.monthlyPrice ?? def.monthlyPrice
  if (!monthly) return null
  if (plan === "DEVELOPER") return `From $${monthly} per project / mo`
  return `From $${monthly}/mo`
}
